import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useOutlet } from '../lib/OutletContext';
import { getRecipe, createRecipe, updateRecipe, publishRecipe, rollupRecipeCost, getIngredients, getPreps } from '../lib/api';
import { BookOpen, Plus, Trash2, Save, Check, Calculator, AlertCircle, ArrowLeft, Package, ChefHat, Clock } from 'lucide-react';
import { formatCurrency, formatNumber } from '../lib/utils';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import logger from '../lib/logger';

// Freshness threshold in hours - costs older than this are considered stale
const FRESHNESS_THRESHOLD_HOURS = 24;

/**
 * Format a timestamp into a human-readable "time ago" string
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {string} Formatted time ago string
 */
const timeAgo = (timestamp) => {
  if (!timestamp) return null;
  
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  
  // Handle future dates or invalid dates
  if (diffMs < 0 || isNaN(diffMs)) return null;
  
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  
  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffMinutes < 60) {
    return `${diffMinutes} min${diffMinutes !== 1 ? 's' : ''} ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
  } else if (diffDays < 7) {
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } else {
    // For older dates, show the actual date
    return then.toLocaleDateString();
  }
};

/**
 * Determine if a cost timestamp is considered "fresh" (within threshold)
 * @param {string|Date} timestamp - ISO timestamp or Date object
 * @returns {boolean} True if fresh, false if stale
 */
const isCostFresh = (timestamp) => {
  if (!timestamp) return false;
  
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffHours = diffMs / (1000 * 60 * 60);
  
  return diffHours < FRESHNESS_THRESHOLD_HOURS;
};

const RECIPE_TYPES = [
  { value: 'MENU_ITEM', label: 'Menu Item' },
  { value: 'PREP', label: 'Prep Recipe' },
];

const YIELD_UNITS = ['portion', 'gm', 'ml', 'pcs', 'kg', 'l'];

// Base unit conversion mapping
const UNIT_CONVERSIONS = {
  // Weight conversions
  'kg': { baseUnit: 'gm', multiplier: 1000, display: 'gm' },
  'g': { baseUnit: 'gm', multiplier: 1, display: 'gm' },
  'gm': { baseUnit: 'gm', multiplier: 1, display: 'gm' },
  'gram': { baseUnit: 'gm', multiplier: 1, display: 'gm' },
  'grams': { baseUnit: 'gm', multiplier: 1, display: 'gm' },
  // Volume conversions
  'l': { baseUnit: 'ml', multiplier: 1000, display: 'ml' },
  'ltr': { baseUnit: 'ml', multiplier: 1000, display: 'ml' },
  'liter': { baseUnit: 'ml', multiplier: 1000, display: 'ml' },
  'litre': { baseUnit: 'ml', multiplier: 1000, display: 'ml' },
  'ml': { baseUnit: 'ml', multiplier: 1, display: 'ml' },
  // Count units
  'pcs': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
  'piece': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
  'pieces': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
  'nos': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
  'unit': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
  'units': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
  'ea': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
  'each': { baseUnit: 'pcs', multiplier: 1, display: 'pcs' },
};

// Get the recipe base unit for an ingredient
const getRecipeBaseUnit = (ingredientUnit) => {
  if (!ingredientUnit) return 'gm';
  const normalized = ingredientUnit.toLowerCase().trim();
  const conversion = UNIT_CONVERSIONS[normalized];
  return conversion ? conversion.display : ingredientUnit;
};

// Get conversion multiplier from ingredient unit to recipe base unit
const getConversionMultiplier = (ingredientUnit) => {
  if (!ingredientUnit) return 1;
  const normalized = ingredientUnit.toLowerCase().trim();
  const conversion = UNIT_CONVERSIONS[normalized];
  return conversion ? conversion.multiplier : 1;
};

// Convert price from ingredient unit to recipe base unit
const convertPriceToBaseUnit = (pricePerUnit, ingredientUnit) => {
  const multiplier = getConversionMultiplier(ingredientUnit);
  if (multiplier === 1) return pricePerUnit;
  // If ingredient is in kg (multiplier 1000), price per gm = price per kg / 1000
  return pricePerUnit / multiplier;
};

const RecipeBuilder = () => {
  const navigate = useNavigate();
  const { id: recipeId } = useParams();
  const { selectedOutlet } = useOutlet();
  const isNew = !recipeId || recipeId === 'new';

  // Form state
  const [recipeName, setRecipeName] = useState('');
  const [recipeType, setRecipeType] = useState('MENU_ITEM');
  const [yieldQty, setYieldQty] = useState(1);
  const [yieldUnit, setYieldUnit] = useState('portion');
  const [lines, setLines] = useState([]);
  const [status, setStatus] = useState('DRAFT');
  const [version, setVersion] = useState(1);
  const [recipeConfidence, setRecipeConfidence] = useState('LOW');
  const [costing, setCosting] = useState({ 
    unit_cost: 0, 
    batch_cost: 0, 
    missing_cost_lines: 0,
    costing_status: 'PENDING',
    cost_source: 'UNKNOWN',
    last_cost_rollup_at: null,
    last_computed_at: null,
    // Authoritative diagnostics from backend (May 6 2026 — 1OS doctrine).
    // line_costs[] is the canonical per-line truth. Frontend renders these
    // when present; falls back to local linesCostInfo only before first
    // Calculate (or after a line edit invalidates the snapshot).
    line_costs: [],
    theoretical_cost_partial: false,
    unit_mismatch_lines: 0,
  });
  
  // Reference data
  const [ingredients, setIngredients] = useState([]);
  const [preps, setPreps] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [rollingUp, setRollingUp] = useState(false);
  const [showIngredientPicker, setShowIngredientPicker] = useState(false);
  const [showPrepPicker, setShowPrepPicker] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [ingredientsResponse, prepsData] = await Promise.all([
          selectedOutlet ? getIngredients(selectedOutlet) : { ingredients: [] },
          getPreps()
        ]);
        // Handle new response format: { ingredients: [], count, sources }
        const ingredientsList = ingredientsResponse?.ingredients || ingredientsResponse || [];
        setIngredients(Array.isArray(ingredientsList) ? ingredientsList : []);
        setPreps(prepsData || []);
      } catch (error) {
        logger.error('[RecipeBuilder] Failed to load reference data:', error);
      }
    };
    loadReferenceData();
  }, [selectedOutlet]);

  // Load existing recipe
  useEffect(() => {
    const loadRecipe = async () => {
      if (isNew) {
        setLoading(false);
        return;
      }
      
      try {
        const data = await getRecipe(recipeId);
        setRecipeName(data.recipe_name || '');
        setRecipeType(data.recipe_type || 'MENU_ITEM');
        setYieldQty(data.yields?.yield_qty || 1);
        setYieldUnit(data.yields?.yield_unit || 'portion');
        setLines(data.lines || []);
        setStatus(data.status || 'DRAFT');
        setVersion(data.version || 1);
        setRecipeConfidence(data.recipe_confidence || 'LOW');
        setCosting(data.costing || { 
          unit_cost: 0, 
          batch_cost: 0, 
          missing_cost_lines: 0,
          costing_status: 'PENDING',
          cost_source: 'UNKNOWN',
          last_cost_rollup_at: null,
          last_computed_at: null,
          line_costs: [],
          theoretical_cost_partial: false,
          unit_mismatch_lines: 0,
        });
      } catch (error) {
        toast.error('Failed to load recipe');
        logger.error('[RecipeBuilder] Error:', error);
        navigate('/recipes');
      } finally {
        setLoading(false);
      }
    };
    
    loadRecipe();
  }, [recipeId, isNew, navigate]);

  // Invalidate the authoritative backend cost snapshot whenever the user
  // mutates the recipe lines (add / edit qty / remove). Doctrine: backend is
  // the source of truth. Once any line changes, the previously-computed
  // line_costs[] no longer matches the recipe — we drop it and fall back to
  // the live frontend estimate, surfacing a "Click Calculate to refresh"
  // banner. This is safer than showing operators stale-but-pretty numbers.
  const invalidateAuthoritativeCosts = () => {
    setCosting((c) => {
      // No-op when there's nothing authoritative to invalidate yet.
      if (!c.line_costs || c.line_costs.length === 0) return c;
      return {
        ...c,
        line_costs: [],
        costing_status: 'STALE',
      };
    });
  };

  // Handlers
  const handleAddIngredientLine = (ingredient) => {
    // Convert to recipe base unit (gm, ml, or pcs)
    const recipeBaseUnit = getRecipeBaseUnit(ingredient.base_unit);
    const conversionMultiplier = getConversionMultiplier(ingredient.base_unit);
    
    // Store original unit info for price conversion
    const newLine = {
      line_id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      line_type: 'INGREDIENT',
      ingredient_id: ingredient.ingredient_id,
      ingredient_name: ingredient.ingredient_name,
      prep_id: null,
      acts_as_ingredient_id: null,
      qty: 1,
      base_unit: recipeBaseUnit, // Use converted base unit (gm, ml, pcs)
      original_unit: ingredient.base_unit, // Store original for price conversion
      conversion_multiplier: conversionMultiplier, // For cost calculation
    };
    setLines([...lines, newLine]);
    setShowIngredientPicker(false);
    setSearchTerm('');
    invalidateAuthoritativeCosts();
  };

  const handleAddPrepLine = (prep) => {
    // Convert to recipe base unit (gm, ml, or pcs)
    const recipeBaseUnit = getRecipeBaseUnit(prep.base_unit);
    const conversionMultiplier = getConversionMultiplier(prep.base_unit);
    
    const newLine = {
      line_id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`,
      line_type: 'PREP',
      ingredient_id: null,
      ingredient_name: null,
      prep_id: prep.prep_id,
      prep_name: prep.prep_name,
      acts_as_ingredient_id: prep.acts_as_ingredient_id,
      qty: 1,
      base_unit: recipeBaseUnit, // Use converted base unit (gm, ml, pcs)
      original_unit: prep.base_unit, // Store original for price conversion
      conversion_multiplier: conversionMultiplier, // For cost calculation
    };
    setLines([...lines, newLine]);
    setShowPrepPicker(false);
    setSearchTerm('');
    invalidateAuthoritativeCosts();
  };

  const handleUpdateLineQty = (lineId, qty) => {
    setLines(lines.map(line => 
      line.line_id === lineId ? { ...line, qty: parseFloat(qty) || 0 } : line
    ));
    invalidateAuthoritativeCosts();
  };

  const handleRemoveLine = (lineId) => {
    setLines(lines.filter(line => line.line_id !== lineId));
    invalidateAuthoritativeCosts();
  };

  const handleSave = async (asDraft = true) => {
    if (!recipeName.trim()) {
      toast.error('Recipe name is required');
      return;
    }
    if (yieldQty <= 0) {
      toast.error('Yield quantity must be greater than 0');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        recipe_name: recipeName.trim(),
        recipe_type: recipeType,
        outlet_id: selectedOutlet || null,
        yields: { yield_qty: yieldQty, yield_unit: yieldUnit },
        lines: lines.map(line => ({
          line_id: line.line_id,
          line_type: line.line_type,
          ingredient_id: line.ingredient_id,
          prep_id: line.prep_id,
          acts_as_ingredient_id: line.acts_as_ingredient_id,
          qty: line.qty,
          base_unit: line.base_unit, // Recipe base unit (gm, ml, pcs)
          original_unit: line.original_unit, // Original ingredient unit (kg, l)
          conversion_multiplier: line.conversion_multiplier, // For price conversion
        })),
        status: asDraft ? 'DRAFT' : status,
        recipe_confidence: recipeConfidence,
      };

      if (isNew) {
        const result = await createRecipe(payload);
        toast.success('Recipe created');
        navigate(`/recipes/${result.recipe_id}`);
      } else {
        await updateRecipe(recipeId, {
          recipe_name: payload.recipe_name,
          recipe_type: payload.recipe_type,
          yields: payload.yields,
          lines: payload.lines,
          recipe_confidence: payload.recipe_confidence,
        });
        toast.success('Recipe saved');
        // Reload to get updated data
        const updated = await getRecipe(recipeId);
        setCosting(updated.costing || costing);
        setVersion(updated.version || version);
        setRecipeConfidence(updated.recipe_confidence || recipeConfidence);
      }
    } catch (error) {
      toast.error('Failed to save recipe');
      logger.error('[RecipeBuilder] Save error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    // Validation for publish
    if (lines.length === 0) {
      toast.error('Recipe must have at least one line to publish');
      return;
    }
    const hasZeroQty = lines.some(line => line.qty <= 0);
    if (hasZeroQty) {
      toast.error('All line quantities must be greater than 0');
      return;
    }

    setPublishing(true);
    try {
      // Save first if needed
      if (isNew) {
        await handleSave(true);
        return;
      }
      
      await publishRecipe(recipeId);
      toast.success('Recipe published');
      setStatus('PUBLISHED');
    } catch (error) {
      toast.error('Failed to publish recipe');
      logger.error('[RecipeBuilder] Publish error:', error);
    } finally {
      setPublishing(false);
    }
  };

  const handleRollupCost = async () => {
    if (isNew) {
      toast.error('Save recipe first to calculate costs');
      return;
    }

    setRollingUp(true);
    try {
      const result = await rollupRecipeCost(recipeId);
      // 1OS doctrine: backend is the source of truth for costing. Capture the
      // full authoritative payload — line_costs[], theoretical_cost_partial,
      // and unit_mismatch_lines — so per-line cells, chips, and the summary
      // diagnostics all read from the same authoritative snapshot.
      setCosting({
        unit_cost: result.unit_cost,
        batch_cost: result.batch_cost,
        missing_cost_lines: result.missing_cost_lines || 0,
        costing_status: result.costing_status || 'FRESH',
        cost_source: result.cost_source || 'UNKNOWN',
        last_cost_rollup_at: new Date().toISOString(),
        last_computed_at: new Date().toISOString(),
        line_costs: Array.isArray(result.line_costs) ? result.line_costs : [],
        theoretical_cost_partial: !!result.theoretical_cost_partial,
        unit_mismatch_lines: result.unit_mismatch_lines || 0,
      });
      toast.success('Cost calculated');
    } catch (error) {
      toast.error('Failed to calculate cost');
      logger.error('[RecipeBuilder] Rollup error:', error);
    } finally {
      setRollingUp(false);
    }
  };

  // Compute costing status for display
  const costingStatusInfo = useMemo(() => {
    // Get the timestamp from persisted costing fields
    const lastComputedAt = costing.last_cost_rollup_at || costing.last_computed_at;
    const unitCost = costing.unit_cost || 0;
    const batchCost = costing.batch_cost || 0;
    const missingLines = costing.missing_cost_lines || 0;
    const costingStatus = costing.costing_status || 'PENDING';
    
    // Case 1: Never computed - no timestamp and zero/null costs
    if (!lastComputedAt && unitCost === 0 && batchCost === 0) {
      return {
        type: 'never',
        icon: 'warning',
        color: 'amber',
        title: 'Never computed',
        subtitle: 'Click "Calculate" to compute recipe cost'
      };
    }
    
    // Case 2: Check freshness based on timestamp
    if (lastComputedAt) {
      const fresh = isCostFresh(lastComputedAt);
      const timeAgoStr = timeAgo(lastComputedAt);
      
      // If explicitly stale or missing lines, show warning
      if (costingStatus === 'STALE' || missingLines > 0) {
        return {
          type: 'stale',
          icon: 'warning',
          color: 'amber',
          title: 'Cost outdated',
          subtitle: missingLines > 0 
            ? `${missingLines} line${missingLines !== 1 ? 's' : ''} missing pricing` 
            : `Updated ${timeAgoStr || 'some time ago'}`
        };
      }
      
      // Check if timestamp is older than threshold
      if (!fresh) {
        return {
          type: 'stale',
          icon: 'warning',
          color: 'amber',
          title: 'Cost outdated',
          subtitle: `Last updated ${timeAgoStr || 'over 24 hours ago'}`
        };
      }
      
      // Fresh cost
      return {
        type: 'fresh',
        icon: 'check',
        color: 'emerald',
        title: timeAgoStr === 'just now' ? 'Updated just now' : `Updated ${timeAgoStr}`,
        subtitle: costing.cost_source && costing.cost_source !== 'UNKNOWN' 
          ? `Source: ${costing.cost_source.toLowerCase().replace('_', ' ')}`
          : null
      };
    }
    
    // Fallback: Pending state
    return {
      type: 'pending',
      icon: 'warning',
      color: 'zinc',
      title: 'Cost pending',
      subtitle: 'Save the recipe to calculate costs'
    };
  }, [costing]);

  // Computed values - track which lines have missing costs
  // Convert prices from ingredient unit (kg, l) to recipe unit (gm, ml)
  const linesCostInfo = lines.map(line => {
    const lineItem = line.line_type === 'INGREDIENT'
      ? ingredients.find(i => i.ingredient_id === line.ingredient_id)
      : preps.find(p => p.prep_id === line.prep_id);
    
    // Get original price per ingredient unit (e.g., per kg)
    const originalUnitCost = lineItem?.avg_cost || 0;
    
    // Convert to price per recipe base unit (e.g., per gm)
    // If ingredient is in kg and price is ₹100/kg, recipe unit gm should be ₹0.10/gm
    const conversionMultiplier = line.conversion_multiplier || 1;
    const convertedUnitCost = conversionMultiplier > 1 
      ? originalUnitCost / conversionMultiplier 
      : originalUnitCost;
    
    const hasPricing = originalUnitCost > 0;
    const pricingSource = lineItem?.pricing_source || 'NONE';
    
    return {
      lineId: line.line_id,
      unitCost: convertedUnitCost, // Price per recipe base unit (gm, ml, pcs)
      originalUnitCost, // Price per ingredient unit (kg, l)
      hasPricing,
      pricingSource,
      lineCost: line.qty * convertedUnitCost, // Cost for this line
      conversionMultiplier,
      originalUnit: line.original_unit,
      recipeUnit: line.base_unit,
    };
  });
  
  const estimatedBatchCost = linesCostInfo.reduce((sum, info) => sum + info.lineCost, 0);
  const estimatedUnitCost = yieldQty > 0 ? estimatedBatchCost / yieldQty : 0;
  const linesWithMissingCost = linesCostInfo.filter(info => !info.hasPricing).length;

  // ──────────────────────────────────────────────────────────────────────
  // Authoritative-cost resolver (1OS doctrine — May 6 2026).
  //   Backend = costing truth. After Calculate, costing.line_costs[] is the
  //   canonical per-line cost. We index it by line_id for O(1) lookup and
  //   render those values in the EST. COST cell. Local linesCostInfo is the
  //   pre-Calculate "live estimate" fallback only.
  // ──────────────────────────────────────────────────────────────────────
  const authoritativeByLineId = useMemo(() => {
    const m = new Map();
    (costing.line_costs || []).forEach((lc) => {
      if (lc && lc.line_id) m.set(lc.line_id, lc);
    });
    return m;
  }, [costing.line_costs]);

  const hasAuthoritativeCosts = authoritativeByLineId.size > 0;

  // Resolve a single line's display cost. Returns { cost, source, kind }.
  //   kind: 'authoritative' (from backend) | 'estimate' (local) | 'unavailable'
  const resolveLineCost = (line) => {
    const auth = authoritativeByLineId.get(line.line_id);
    if (auth) {
      return {
        cost: auth.cost ?? 0,
        unitCost: auth.unit_cost ?? 0,
        source: auth.source || 'UNKNOWN',
        kind: 'authoritative',
      };
    }
    // Fallback to local estimate (pre-Calculate or post-edit invalidation).
    const est = linesCostInfo.find((i) => i.lineId === line.line_id);
    if (est && est.hasPricing) {
      return {
        cost: est.lineCost,
        unitCost: est.unitCost,
        source: 'ESTIMATE',
        kind: 'estimate',
      };
    }
    return { cost: 0, unitCost: 0, source: 'UNKNOWN', kind: 'unavailable' };
  };

  // Filter helpers
  const filteredIngredients = ingredients.filter(i => 
    i.ingredient_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredPreps = preps.filter(p => 
    p.prep_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recipe-builder-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/recipes')}
            className="p-2 rounded-md hover:bg-[#27272a] transition-colors"
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-black tracking-tight" data-testid="recipe-builder-title">
              {isNew ? 'New Recipe' : recipeName || 'Edit Recipe'}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span className={cn(
                'inline-flex px-2 py-0.5 rounded text-xs font-medium border',
                status === 'PUBLISHED' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                status === 'ARCHIVED' ? 'bg-red-500/10 text-red-400 border-red-500/20' :
                'bg-zinc-500/10 text-zinc-400 border-zinc-500/20'
              )}>
                {status}
              </span>
              <span className={cn(
                'inline-flex px-2 py-0.5 rounded text-xs font-medium border',
                recipeConfidence === 'HIGH' ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' :
                recipeConfidence === 'MEDIUM' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                'bg-amber-500/10 text-amber-400 border-amber-500/20'
              )} data-testid="recipe-confidence-badge">
                {recipeConfidence}
              </span>
              <span className="text-xs text-zinc-500">v{version}</span>
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => handleSave(true)}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-[#27272a] hover:bg-[#3f3f46] rounded-md transition-colors text-sm font-medium"
            data-testid="save-draft-button"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          {status === 'DRAFT' && (
            <button
              onClick={handlePublish}
              disabled={publishing || lines.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 rounded-md transition-colors text-sm font-bold text-white disabled:opacity-50"
              data-testid="publish-button"
            >
              <Check className="w-4 h-4" />
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Info */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6">
            <h2 className="text-lg font-bold mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">Recipe Name *</label>
                <input
                  type="text"
                  value={recipeName}
                  onChange={(e) => setRecipeName(e.target.value)}
                  className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                  placeholder="e.g., Margherita Pizza"
                  data-testid="recipe-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Type</label>
                <select
                  value={recipeType}
                  onChange={(e) => setRecipeType(e.target.value)}
                  className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none"
                  data-testid="recipe-type-select"
                >
                  {RECIPE_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2">Yield Qty</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={yieldQty}
                    onChange={(e) => setYieldQty(parseFloat(e.target.value) || 1)}
                    className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none"
                    data-testid="yield-qty-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2">Unit</label>
                  <select
                    value={yieldUnit}
                    onChange={(e) => setYieldUnit(e.target.value)}
                    className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none"
                    data-testid="yield-unit-select"
                  >
                    {YIELD_UNITS.map(u => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium mb-2">
                  Confidence
                  <span className="text-xs text-zinc-500 font-normal ml-2">
                    Operator trust level. LOW = usable, not audited. Manual only.
                  </span>
                </label>
                <select
                  value={recipeConfidence}
                  onChange={(e) => setRecipeConfidence(e.target.value)}
                  className="w-full px-4 py-2 bg-[#09090b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none"
                  data-testid="recipe-confidence-select"
                >
                  <option value="LOW">LOW — first operational version, usable, not audited</option>
                  <option value="MEDIUM">MEDIUM — reviewed by kitchen / procurement</option>
                  <option value="HIGH">HIGH — stable, governed, trusted</option>
                </select>
              </div>
            </div>
          </div>

          {/* Lines Editor */}
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Recipe Lines</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowIngredientPicker(true); setShowPrepPicker(false); }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-500 rounded-md transition-colors text-sm font-medium"
                  data-testid="add-ingredient-button"
                >
                  <Package className="w-4 h-4" />
                  Add Ingredient
                </button>
                <button
                  onClick={() => { setShowPrepPicker(true); setShowIngredientPicker(false); }}
                  className="flex items-center gap-2 px-3 py-1.5 bg-purple-500/10 hover:bg-purple-500/20 text-purple-400 rounded-md transition-colors text-sm font-medium"
                  data-testid="add-prep-button"
                >
                  <ChefHat className="w-4 h-4" />
                  Add Prep
                </button>
              </div>
            </div>

            {/* Ingredient Picker Modal */}
            {showIngredientPicker && (
              <div className="mb-4 p-4 bg-[#09090b] border border-[#3f3f46] rounded-lg">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search ingredients..."
                  className="w-full px-4 py-2 bg-[#18181b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none mb-3"
                  autoFocus
                  data-testid="ingredient-search-input"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredIngredients.length === 0 ? (
                    <div className="text-center py-4">
                      <p className="text-sm text-zinc-500">No ingredients found</p>
                      {ingredients.length === 0 && (
                        <p className="text-xs text-zinc-600 mt-1">
                          Check Control Room integration or initialize opening stock first.
                        </p>
                      )}
                    </div>
                  ) : (
                    filteredIngredients.slice(0, 20).map(ing => {
                      const hasPricing = ing.avg_cost > 0;
                      const recipeUnit = getRecipeBaseUnit(ing.base_unit);
                      const conversionMultiplier = getConversionMultiplier(ing.base_unit);
                      const pricePerRecipeUnit = conversionMultiplier > 1 
                        ? ing.avg_cost / conversionMultiplier 
                        : ing.avg_cost;
                      
                      return (
                        <button
                          key={ing.ingredient_id}
                          onClick={() => handleAddIngredientLine(ing)}
                          className="w-full flex items-center justify-between p-2 hover:bg-[#18181b] rounded transition-colors text-left"
                          data-testid="ingredient-option"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-sm">{ing.ingredient_name}</span>
                            {hasPricing ? (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400">
                                ₹{formatNumber(pricePerRecipeUnit)}/{recipeUnit}
                              </span>
                            ) : (
                              <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-700/50 text-zinc-500">
                                No price
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-zinc-500">{recipeUnit}</span>
                        </button>
                      );
                    })
                  )}
                </div>
                <button
                  onClick={() => { setShowIngredientPicker(false); setSearchTerm(''); }}
                  className="mt-2 text-xs text-zinc-500 hover:text-zinc-400"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Prep Picker Modal */}
            {showPrepPicker && (
              <div className="mb-4 p-4 bg-[#09090b] border border-[#3f3f46] rounded-lg">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search preps..."
                  className="w-full px-4 py-2 bg-[#18181b] border border-[#3f3f46] rounded-md focus:border-emerald-500 outline-none mb-3"
                  autoFocus
                  data-testid="prep-search-input"
                />
                <div className="max-h-48 overflow-y-auto space-y-1">
                  {filteredPreps.length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-4">No preps found</p>
                  ) : (
                    filteredPreps.slice(0, 20).map(prep => (
                      <button
                        key={prep.prep_id}
                        onClick={() => handleAddPrepLine(prep)}
                        className="w-full flex items-center justify-between p-2 hover:bg-[#18181b] rounded transition-colors text-left"
                        data-testid="prep-option"
                      >
                        <span className="text-sm">{prep.prep_name}</span>
                        <span className="text-xs text-zinc-500">{prep.base_unit}</span>
                      </button>
                    ))
                  )}
                </div>
                <button
                  onClick={() => { setShowPrepPicker(false); setSearchTerm(''); }}
                  className="mt-2 text-xs text-zinc-500 hover:text-zinc-400"
                >
                  Cancel
                </button>
              </div>
            )}

            {/* Lines Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="border-b border-[#27272a]">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-zinc-400 uppercase tracking-wider">Item</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider w-32">Qty</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Unit</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-zinc-400 uppercase tracking-wider">Est. Cost</th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-zinc-400 uppercase tracking-wider w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#27272a]">
                  {lines.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-3 py-8 text-center text-zinc-500 text-sm">
                        No lines added. Click &quot;Add Ingredient&quot; or &quot;Add Prep&quot; to start building.
                      </td>
                    </tr>
                  ) : (
                    lines.map((line) => {
                      const resolved = resolveLineCost(line);
                      const isAuthoritative = resolved.kind === 'authoritative';
                      const isUnitMismatch = resolved.source === 'UNIT_MISMATCH';
                      const isPrepRollup = resolved.source === 'PREP_ROLLUP';
                      const isCycle = resolved.source === 'PREP_CYCLE_DETECTED';
                      const isUnavailable = resolved.kind === 'unavailable';
                      // Color: red for unit mismatch, purple for prep rollup,
                      // emerald for normal authoritative/estimate, zinc when missing.
                      const costColor = isUnitMismatch
                        ? 'text-red-400'
                        : isPrepRollup
                          ? 'text-purple-300'
                          : isUnavailable
                            ? 'text-zinc-500'
                            : 'text-emerald-400';
                      return (
                      <tr key={line.line_id} className="hover:bg-[#09090b]" data-testid="recipe-line-row">
                        <td className="px-3 py-3">
                          <span className={cn(
                            'inline-flex px-2 py-0.5 rounded text-xs font-medium',
                            line.line_type === 'INGREDIENT' 
                              ? 'bg-emerald-500/10 text-emerald-500' 
                              : 'bg-purple-500/10 text-purple-400'
                          )}>
                            {line.line_type === 'INGREDIENT' ? 'Ingredient' : 'Prep'}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-medium">
                              {line.line_type === 'INGREDIENT' ? line.ingredient_name : line.prep_name}
                            </span>
                            {isPrepRollup && (
                              <span
                                className="text-xs px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300"
                                title="Cost rolled up from this prep's own ingredient lines (theoretical — no batch produced yet)"
                                data-testid={`recipe-line-source-prep-rollup-${line.line_id}`}
                              >
                                Theoretical prep cost
                              </span>
                            )}
                            {isUnitMismatch && (
                              <span
                                className="text-xs px-1.5 py-0.5 rounded bg-red-500/10 text-red-400"
                                title={`Unit mismatch — recipe line is in ${line.base_unit} but the ingredient is priced in a different unit class. Cost cannot be computed; fix the unit before publishing.`}
                                data-testid={`recipe-line-source-unit-mismatch-${line.line_id}`}
                              >
                                Unit mismatch
                              </span>
                            )}
                            {isCycle && (
                              <span
                                className="text-xs px-1.5 py-0.5 rounded bg-red-500/10 text-red-400"
                                title="Prep references itself (directly or via another prep). Break the cycle to enable costing."
                                data-testid={`recipe-line-source-prep-cycle-${line.line_id}`}
                              >
                                Prep cycle
                              </span>
                            )}
                            {isUnavailable && (
                              <span
                                className="text-xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400"
                                title="No pricing data available — neither in Control Room nor in this prep's recipe."
                                data-testid={`recipe-line-cost-unavailable-${line.line_id}`}
                              >
                                Cost unavailable
                              </span>
                            )}
                            {!isAuthoritative && !isUnavailable && hasAuthoritativeCosts === false && (
                              <span
                                className="text-xs px-1.5 py-0.5 rounded bg-zinc-500/10 text-zinc-400"
                                title="Live frontend estimate. Click Calculate to use authoritative backend costing."
                                data-testid={`recipe-line-source-estimate-${line.line_id}`}
                              >
                                Estimate
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.qty}
                            onChange={(e) => handleUpdateLineQty(line.line_id, e.target.value)}
                            className="w-full px-2 py-1 bg-[#09090b] border border-[#3f3f46] rounded text-right font-mono text-sm focus:border-emerald-500 outline-none"
                            data-testid="line-qty-input"
                          />
                        </td>
                        <td className="px-3 py-3 text-right">
                          <span className="text-sm text-zinc-400">{line.base_unit}</span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          {isUnavailable ? (
                            <span className="text-sm text-zinc-500" data-testid={`recipe-line-cost-${line.line_id}`}>—</span>
                          ) : (
                            <span
                              className={cn('text-sm font-mono', costColor)}
                              data-testid={`recipe-line-cost-${line.line_id}`}
                              data-cost-source={resolved.source}
                            >
                              {formatCurrency(resolved.cost)}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <button
                            onClick={() => handleRemoveLine(line.line_id)}
                            className="p-1 rounded hover:bg-red-500/10 text-red-400 transition-colors"
                            data-testid="remove-line-button"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Cost Panel */}
        <div className="space-y-6">
          <div className="bg-[#18181b] border border-[#27272a] rounded-lg p-6 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">Cost Summary</h2>
              <button
                onClick={handleRollupCost}
                disabled={rollingUp || isNew}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 rounded-md transition-colors text-sm font-medium disabled:opacity-50"
                data-testid="rollup-cost-button"
              >
                <Calculator className="w-4 h-4" />
                {rollingUp ? 'Calculating...' : 'Calculate'}
              </button>
            </div>

            <div className="space-y-4">
              {/* Costing Status Indicator - P0 Feature */}
              <div 
                className={cn(
                  "p-3 rounded-lg flex items-start gap-3",
                  costingStatusInfo.color === 'emerald' && "bg-emerald-500/10 border border-emerald-500/20",
                  costingStatusInfo.color === 'amber' && "bg-amber-500/10 border border-amber-500/20",
                  costingStatusInfo.color === 'zinc' && "bg-zinc-500/10 border border-zinc-500/20"
                )}
                data-testid="costing-status-indicator"
              >
                {costingStatusInfo.icon === 'check' ? (
                  <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                ) : costingStatusInfo.icon === 'warning' ? (
                  <AlertCircle className={cn(
                    "w-5 h-5 flex-shrink-0 mt-0.5",
                    costingStatusInfo.color === 'amber' ? "text-amber-500" : "text-zinc-500"
                  )} />
                ) : (
                  <Clock className="w-5 h-5 text-zinc-500 flex-shrink-0 mt-0.5" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={cn(
                    "text-sm font-medium",
                    costingStatusInfo.color === 'emerald' && "text-emerald-500",
                    costingStatusInfo.color === 'amber' && "text-amber-500",
                    costingStatusInfo.color === 'zinc' && "text-zinc-400"
                  )} data-testid="costing-status-title">
                    {costingStatusInfo.type === 'fresh' ? '✔' : '⚠'} {costingStatusInfo.title}
                  </p>
                  {costingStatusInfo.subtitle && (
                    <p className={cn(
                      "text-xs mt-0.5",
                      costingStatusInfo.color === 'emerald' && "text-emerald-400",
                      costingStatusInfo.color === 'amber' && "text-amber-400",
                      costingStatusInfo.color === 'zinc' && "text-zinc-500"
                    )} data-testid="costing-status-subtitle">
                      {costingStatusInfo.subtitle}
                    </p>
                  )}
                </div>
              </div>

              <div className="p-4 bg-[#09090b] border border-[#27272a] rounded-lg">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Unit Cost</p>
                <p className="text-2xl font-mono font-bold text-emerald-500" data-testid="unit-cost-display">
                  {formatCurrency(costing.unit_cost || estimatedUnitCost)}
                </p>
                <p className="text-xs text-zinc-500 mt-1">per {yieldUnit}</p>
              </div>

              <div className="p-4 bg-[#09090b] border border-[#27272a] rounded-lg">
                <p className="text-xs text-zinc-400 uppercase tracking-wider mb-1">Batch Cost</p>
                <p className="text-xl font-mono font-bold" data-testid="batch-cost-display">
                  {formatCurrency(costing.batch_cost || estimatedBatchCost)}
                </p>
              </div>

              {/* 1OS doctrine diagnostics — May 6 2026.
                  Surfaced from costing.line_costs[] / theoretical_cost_partial /
                  unit_mismatch_lines so operators see exactly what the backend
                  saw when it computed cost. */}

              {/* Recipe lines edited after a backend Calculate — frontend has
                  cleared line_costs and is now showing live estimates. */}
              {!hasAuthoritativeCosts && costing.last_cost_rollup_at && lines.length > 0 && (
                <div
                  className="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg flex items-start gap-2"
                  data-testid="costing-stale-after-edit-banner"
                >
                  <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-amber-500">Recipe changed after last calculation</p>
                    <p className="text-amber-400 mt-0.5">
                      Showing live estimates. Click Calculate to refresh authoritative costs.
                    </p>
                  </div>
                </div>
              )}

              {costing.unit_mismatch_lines > 0 && (
                <div
                  className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-2"
                  data-testid="costing-unit-mismatch-banner"
                >
                  <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-red-400">
                      {costing.unit_mismatch_lines} line{costing.unit_mismatch_lines !== 1 ? 's' : ''} have unit mismatch
                    </p>
                    <p className="text-red-300 mt-0.5">
                      Recipe units don&apos;t match the ingredient&apos;s pricing unit class. Cost is excluded.
                    </p>
                  </div>
                </div>
              )}

              {costing.theoretical_cost_partial && (
                <div
                  className="p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg flex items-start gap-2"
                  data-testid="costing-theoretical-partial-banner"
                >
                  <AlertCircle className="w-4 h-4 text-purple-300 flex-shrink-0 mt-0.5" />
                  <div className="text-xs">
                    <p className="font-medium text-purple-300">Theoretical cost — partial</p>
                    <p className="text-purple-200/80 mt-0.5">
                      One or more preps used here have unpriced sub-ingredients. The total reflects only what could be priced.
                    </p>
                  </div>
                </div>
              )}

              {(costing.missing_cost_lines > 0 || linesWithMissingCost > 0) && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-500">Missing Costs</p>
                      <p className="text-xs text-amber-400 mt-1" data-testid="costing-missing-count">
                        {costing.missing_cost_lines || linesWithMissingCost} line{(costing.missing_cost_lines || linesWithMissingCost) !== 1 ? 's' : ''} without cost data
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {lines.some(l => l.qty <= 0) && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-red-400">Zero Qty Lines</p>
                      <p className="text-xs text-red-300 mt-1">
                        All quantities must be greater than 0 to publish
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="pt-4 border-t border-[#27272a]">
                <p className="text-xs text-zinc-500 mb-2">Yield: {formatNumber(yieldQty)} {yieldUnit}</p>
                <p className="text-xs text-zinc-500">Lines: {lines.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecipeBuilder;
